import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahClientInvoices } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { ClientInvoice } from '../models';

interface EstimateRequest {
  invoice_date: string;
}

interface EstimateClient {
  id: number;
  client_name: string;
  total_contribution_amount: number;
  reconciliations: string[];
}

interface EstimateResponse {
  overall_total: number;
  clients: EstimateClient[];
}

interface CreateResponse {
  invoices_count: number;
}

export const clientInvoicesApi = createApi({
  reducerPath: 'clientInvoicesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahClientInvoices }),
  tagTypes: ['client-invoices', 'client-invoice-estimates'],
  endpoints: (builder) => ({
    getClientInvoices: builder.query<ClientInvoice[], Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'client-invoices', id: 'LIST' }],
      transformResponse: (payload) => unwrap<ClientInvoice[]>(payload),
    }),
    getClientInvoice: builder.query<ClientInvoice, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'client-invoices', id }],
      transformResponse: (payload) => unwrap<ClientInvoice>(payload),
    }),
    createClientInvoice: builder.mutation<ClientInvoice, Request>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: [{ type: 'client-invoices', id: 'LIST' }],
      transformResponse: (payload) => unwrap<ClientInvoice>(payload),
    }),
    estimate: builder.query<EstimateResponse, EstimateRequest>({
      query: (params) => ({ url: '/estimate', method: 'GET', params }),
      providesTags: [{ type: 'client-invoice-estimates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<EstimateResponse>(payload),
    }),
    create: builder.mutation<CreateResponse, EstimateRequest & { due_date: string }>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: [
        { type: 'client-invoices', id: 'LIST' },
        { type: 'client-invoice-estimates', id: 'LIST' },
      ],
      transformResponse: (payload) => unwrap<CreateResponse>(payload),
    }),
    updateClientInvoice: builder.mutation<void, { id: string; status_id: string }>({
      query: ({ id, ...body }) => ({ url: `/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'client-invoices', id: 'LIST' },
        { type: 'client-invoices', id },
      ],
    }),
  }),
});

export const {
  useGetClientInvoicesQuery,
  useGetClientInvoiceQuery,
  useEstimateQuery,
  useCreateMutation,
  useUpdateClientInvoiceMutation,
} = clientInvoicesApi;
